import angular from 'angular';
import { ConfigService } from 'app/config/config.service';
import { Cesium } from 'app/cesium';
import constants from '../../shared/utilities/constant';
import * as _ from 'lodash';
import { SpecialSingleTileImageryProvider } from 'app/config/special_single_tile_imagery_provider';

// Available 'type' options for an ImsImageryLayer; NOTE: these correlate to some defaults
// at wireless-manager-api/API/WirelessManager/settings.py- that file is where all the form
// definitions live
const MAP_LAYER_TYPE__ARC_GIS = 'arcgis';
const MAP_LAYER_TYPE__BING_MAPS = 'bingmaps';
const MAP_LAYER_TYPE__MAPBOX = 'mapbox';
const MAP_LAYER_TYPE__OPEN_STREET_MAP = 'osm';
const MAP_LAYER_TYPE__WEB_MAP_TILE = 'wmts';
const MAP_LAYER_TYPE__WEB_MAP = 'wms';
const MAP_LAYER_TYPE__TILE_MAP_SERVICE = 'tms';
const MAP_LAYER_TYPE__CESIUM_ION = 'ion';
const MAP_LAYER_TYPE__SINGLE_TILE = 'singletile';

export const VALID_IMAGERY_TYPES = [
    MAP_LAYER_TYPE__ARC_GIS,
    MAP_LAYER_TYPE__BING_MAPS,
    MAP_LAYER_TYPE__MAPBOX,
    MAP_LAYER_TYPE__OPEN_STREET_MAP,
    MAP_LAYER_TYPE__WEB_MAP_TILE,
    MAP_LAYER_TYPE__WEB_MAP,
    MAP_LAYER_TYPE__TILE_MAP_SERVICE,
    MAP_LAYER_TYPE__CESIUM_ION,
    MAP_LAYER_TYPE__SINGLE_TILE,
];

/**
 * We use this special constant for hiding imagery layers
 * @type {number}
 */
const MAP_LAYER__BASE_HEIGHT_GROUP_INDEX = 0;

// The default (if unspecified) heightGroupIndex is 1000
const MAP_LAYER__DEFAULT_HEIGHT_GROUP_INDEX = constants.ImsImageryHeightGroupIndexes.BASE;

class PrefixProxy {
    proxy;

    constructor(proxy) {
        this.proxy = proxy;
    }

    getURL(resource) {
        const newUrl = this.proxy + resource;
        return newUrl;
    }
}

type ImsImageryLayerOptions =
    | Cesium.TileMapServiceImageryProvider.ConstructorOptions
    | Cesium.ArcGisMapServerImageryProvider.ConstructorOptions
    | Cesium.BingMapsImageryProvider.ConstructorOptions
    | Cesium.MapboxImageryProvider.ConstructorOptions
    | Cesium.OpenStreetMapImageryProvider.ConstructorOptions
    | Cesium.WebMapTileServiceImageryProvider.ConstructorOptions
    | Cesium.WebMapServiceImageryProvider.ConstructorOptions;

// This is the container storing the actual cesium ImageryLayer object
export interface ImsImageryLayer {
    // Imagery layers with a higher height group index will never go below imagery layers of a lower height group
    heightGroupIndex?: number;

    // The friendly name of the map layer
    name?: string;

    // The type of map layer used
    type?: string;

    // The cesium imagery layer stored in the map
    cesiumImageryLayer?: any;

    // Url is used to build the cesium imagery layer
    url?: string;

    // assetId is used by cesiumIon layers
    assetId?: number;

    // Whether or not the layer should show in the list of map layers
    hideInMapLayers?: boolean;

    // Whether or not the layer should show by default
    showByDefault?: boolean;

    //this is here because not all imagery layers have a required URL
    uuid?: string;

    //mapBox mapID
    mapId?: string;

    // singleTile image center, width, height and rotation
    longitude_of_center?: number;
    latitude_of_center?: number;
    width_metres?: number;
    height_metres?: number;
    rotation_degrees?: number;
}

class BaseBingLayerFactory {
    public create(): Promise<Cesium.IonImageryProvider> {
        return new Cesium.createWorldImageryAsync({
            style: Cesium.IonWorldImageryStyle.AERIAL,
        });
    }
}

/**
 * This service handles storing, manipulating and ordering Imagery layers for IMS.
 * Layers are able to be sorted, added and removed within different height groups.
 * ImsImageryLayer objects act as wrappers around Cesium ImageryLayers with additional information
 * such as their heightGroupIndex
 */
export class ImsImageryLayersService {
    // peer level access, used in tests
    private readonly _viewer: Cesium.Viewer;
    imageryLayers: ImsImageryLayer[] = [];
    imageryLayersByHeightGroup: Map<number, ImsImageryLayer[]> = new Map();
    // Used to remember the bing layer when it is present, i.e. is nullable
    private _baseBingCesiumImageryLayer;
    private _bingLayerFactory: BaseBingLayerFactory;

    constructor(
        viewer: Cesium.Viewer,
        bingLayerFactory: BaseBingLayerFactory = new BaseBingLayerFactory(),
    ) {
        this._viewer = viewer;
        this._bingLayerFactory = bingLayerFactory;
    }

    get viewer(): Cesium.Viewer {
        return this._viewer;
    }

    /**
     * Returns the first ImsImageryLayer with a matching uuid
     * Returns null if no matching ImsImageryLayer is found
     * @param {string} uuid
     * @return {any}
     */
    getLayerByUuid(uuid: string) {
        if (uuid) {
            for (let i = 0; i < this.imageryLayers.length; i++) {
                if (this.imageryLayers[i].uuid === uuid) {
                    return this.imageryLayers[i];
                }
            }
        }
        return null;
    }

    public async setRemoveBingBaseLayer(removeBaseBingLayer: boolean): Promise<void> {
        if (removeBaseBingLayer && this._baseBingCesiumImageryLayer) {
            if (this._viewer.imageryLayers.contains(this._baseBingCesiumImageryLayer)) {
                this._viewer.imageryLayers.remove(this._baseBingCesiumImageryLayer);
            }
            this._baseBingCesiumImageryLayer = null;
        }
        if (!removeBaseBingLayer && !this._baseBingCesiumImageryLayer) {
            try {
                // Wait for the bing layer to be ready. This will except if something
                // goes wrong! If we allow this to throw after the cesium the base layer
                // has been added to the Cesium Viewer object, the base layer breaks.
                // Instead of seeing a "blue marble" for Earth, the user can see black space.
                const bingLayer = await this._bingLayerFactory.create();
                this._baseBingCesiumImageryLayer = this._viewer.imageryLayers.addImageryProvider(
                    bingLayer,
                    0,
                );
            } catch (e) {
                // Failed to create the base bing layer. This can sometimes happen if the browser
                // attempting to access the cesium API, and hence the bing imagery, is behind
                // a proxy that either gives a 200 when the request didn't actually succeed,
                // or there's something goes wrong with the proxy's MITM cert offering
                // or something else ???
                console.error('Failed to create bing base layer', e);
            }
        }
    }

    /**
     * Returns whether or not the imagery layer type is one of the supported types
     * @param type imagery layer type (string)
     */
    static isValidImageryLayerType(type: string) {
        return VALID_IMAGERY_TYPES.indexOf(type) >= 0;
    }

    /**
     * Shows an ImsImageryLayer
     * @param {ImsImageryLayer} imsImageryLayer
     * @return {ImsImageryLayer}
     */
    showLayer(imsImageryLayer: ImsImageryLayer) {
        if (imsImageryLayer && imsImageryLayer.cesiumImageryLayer) {
            imsImageryLayer.cesiumImageryLayer.show = true;

            // TODO: a terrible hack for showing our single tile imagery provider
            if (imsImageryLayer.cesiumImageryLayer.imageryProvider['specialShow']) {
                imsImageryLayer.cesiumImageryLayer.imageryProvider['specialShow']();
            }
        }
        return imsImageryLayer;
    }

    /**
     * This raises the provided ImsImageryLayer to the top of its height group.
     * Note that it won't be raised above any ImsImageryLayers in any height groups with a higher index than it
     * @param {ImsImageryLayer} imsImageryLayer
     */
    raiseLayerToTopOfHeightGroup(imsImageryLayer: ImsImageryLayer) {
        // Attempt to get an existing array of layers with this heightGroupIndex number
        const existingHeightGroupArray: ImsImageryLayer[] = this.imageryLayersByHeightGroup.get(
            imsImageryLayer.heightGroupIndex,
        );

        // If there's already an array of imagery for this heightGroupIndex:
        if (existingHeightGroupArray) {
            // Remove if already in there
            if (existingHeightGroupArray.includes(imsImageryLayer)) {
                existingHeightGroupArray.splice(
                    existingHeightGroupArray.indexOf(imsImageryLayer),
                    1,
                );
            }
            // Add imsImageryLayer to the top (highest index) of the array
            existingHeightGroupArray.push(imsImageryLayer);
        } else {
            this.imageryLayersByHeightGroup.set(imsImageryLayer.heightGroupIndex, [
                imsImageryLayer,
            ]);
        }

        if (!this.imageryLayers.includes(imsImageryLayer)) {
            this.imageryLayers.push(imsImageryLayer);
        }

        this._conformCesiumImageryLayers();
    }

    /**
     * Hides any ImsImageryLayers with the base height group index
     */
    hideImageryLayersWithBaseHeightGroupIndex() {
        for (let i = 0; i < this.imageryLayers.length; i++) {
            if (
                this.imageryLayers[i].heightGroupIndex === MAP_LAYER__BASE_HEIGHT_GROUP_INDEX &&
                this.imageryLayers[i].cesiumImageryLayer
            ) {
                this.imageryLayers[i].cesiumImageryLayer.show = false;

                // TODO: a terrible hack for hiding our single tile imagery provider
                if (this.imageryLayers[i].cesiumImageryLayer.imageryProvider['specialHide']) {
                    this.imageryLayers[i].cesiumImageryLayer.imageryProvider['specialHide']();
                }
            }
        }
    }

    /**
     * Adds an ImsImageryLayer to this service and the Cesium Viewer,
     * and creates the appropriate Cesium ImageryLayer if needed.
     * Layers are added on top of all others in their height group
     * @param {ImsImageryLayer} layer
     */
    async addImsImageryLayer(layer: ImsImageryLayer) {
        // Set heightGroupIndex to default if unspecified
        if (layer.heightGroupIndex === null || typeof layer.heightGroupIndex == 'undefined') {
            layer.heightGroupIndex = MAP_LAYER__DEFAULT_HEIGHT_GROUP_INDEX;
        }

        // If a type and a url are specified, but the cesiumImageryLayer doesn't exist yet, then create it
        if (
            (layer.url || !_.isNil(layer.assetId) || layer.mapId || layer.type === 'osm') && //the ever expanding type check
            layer.type &&
            !layer.cesiumImageryLayer
        ) {
            switch (layer.type) {
                case MAP_LAYER_TYPE__ARC_GIS:
                    try {
                        await this._createArcGisLayer(layer);
                    } catch (e) {
                        console.error(e);
                    }
                    break;
                case MAP_LAYER_TYPE__BING_MAPS:
                    try {
                        await this._createBingMapsLayer(layer);
                    } catch (e) {
                        console.error(e);
                    }
                    break;
                case MAP_LAYER_TYPE__TILE_MAP_SERVICE:
                    try {
                        await this._createTileMapServiceLayer(layer);
                    } catch (e) {
                        console.error(e);
                    }
                    break;
                case MAP_LAYER_TYPE__CESIUM_ION:
                    try {
                        await this._createCesiumIonLayer(layer);
                    } catch (e) {
                        console.error(e);
                    }
                    break;

                case MAP_LAYER_TYPE__MAPBOX:
                    this._createMapBoxLayer(layer);
                    break;
                case MAP_LAYER_TYPE__OPEN_STREET_MAP:
                    this._createOpenStreetMapLayer(layer);
                    break;
                case MAP_LAYER_TYPE__WEB_MAP_TILE:
                    this._createWebMapTileLayer(layer);
                    break;
                case MAP_LAYER_TYPE__WEB_MAP:
                    this._createWebMapLayer(layer);
                    break;
                case MAP_LAYER_TYPE__SINGLE_TILE:
                    this._createSingleTileLayer(layer);
                    break;

                default:
                    console.error(
                        `Cannot create ims imagery layer with unknown type: ${layer.type}`,
                    );
            }
        } else if (
            layer.cesiumImageryLayer &&
            !this._viewer.imageryLayers.contains(layer.cesiumImageryLayer)
        ) {
            // Otherwise if the imagerylayer exists and hasn't already been added to the cesium viewer, add it
            this._viewer.imageryLayers.add(layer.cesiumImageryLayer);
            this.imageryLayers.push(layer);
            // Layers are added in at the top. This function is called if a new layer is created as per the above switch
            this.raiseLayerToTopOfHeightGroup(layer);
        }
    }

    /**
     * Creates a Cesium ImageryLayer from an imagery provider and ImsMapLayer object
     * @param cesiumImageryProvider Cesium ImageryProvider
     * @param imsImageryLayer
     * @param show
     * @private
     */
    addImsImageryLayerFromImageryProvider(
        cesiumImageryProvider: Cesium.SingleTileImageryProvider,
        imsImageryLayer: ImsImageryLayer,
        show = true,
    ): ImsImageryLayer {
        // cesium now rejects the readyPromises
        imsImageryLayer.cesiumImageryLayer =
            this._viewer.imageryLayers.addImageryProvider(cesiumImageryProvider);

        // At the moment the height group index defines the show / hide state of the layer.
        // When adding layers, we want to add them in with the correct show state. This improves drawing.
        // Previous all layers were set to show = true then quickly hidden. Under certain circumstances this can
        // cause the layers to partially draw and hang around until something is done to trigger a re-render.
        imsImageryLayer.cesiumImageryLayer.show =
            imsImageryLayer.heightGroupIndex !== MAP_LAYER__BASE_HEIGHT_GROUP_INDEX;

        this.imageryLayers.push(imsImageryLayer);

        this.raiseLayerToTopOfHeightGroup(imsImageryLayer);

        return imsImageryLayer;
    }

    /**
     * Removes an imsImageryLayer from this service and from Cesium
     * @param {ImsImageryLayer} imsImageryLayer
     */
    removeImsImageryLayer(imsImageryLayer: ImsImageryLayer) {
        // Remove the imsImageryLayer from the imageryLayersByHeightGroup object
        const heightGroupImsImageryArray = this.imageryLayersByHeightGroup.get(
            imsImageryLayer.heightGroupIndex,
        );
        if (heightGroupImsImageryArray && heightGroupImsImageryArray.includes(imsImageryLayer)) {
            heightGroupImsImageryArray.splice(
                heightGroupImsImageryArray.indexOf(imsImageryLayer),
                1,
            );

            // Remove heightGroupImsImageryArray entry if it no longer contains any elements
            if (heightGroupImsImageryArray.length === 0) {
                this.imageryLayersByHeightGroup.delete(imsImageryLayer.heightGroupIndex);
            }
        }

        // Remove the imsImageryLayer from the imageryLayers array
        if (this.imageryLayers.includes(imsImageryLayer)) {
            this.imageryLayers.splice(this.imageryLayers.indexOf(imsImageryLayer), 1);
        }

        // Remove the cesium imagery layer from the viewer
        if (
            imsImageryLayer.cesiumImageryLayer &&
            this._viewer.imageryLayers.contains(imsImageryLayer.cesiumImageryLayer)
        ) {
            this._viewer.imageryLayers.remove(imsImageryLayer.cesiumImageryLayer);
        }

        // TODO: a terrible hack for removing our single tile imagery provider
        if (imsImageryLayer.cesiumImageryLayer) {
            if (imsImageryLayer.cesiumImageryLayer.imageryProvider['specialDestroy']) {
                imsImageryLayer.cesiumImageryLayer.imageryProvider['specialDestroy']();
            }
        }
    }

    /**
     * Remove a layer by uuid
     * @param {string} uuid
     * @returns {boolean} true if layer existed and has been removed. false otherwise.
     */
    removeImsImageryLayerByUuid(uuid: string): boolean {
        if (!uuid) {
            throw new Error(`uuid must be truthy`);
        }
        const layer = this.imageryLayers.find((l) => l.uuid === uuid);
        if (layer) {
            this.removeImsImageryLayer(layer);
            return true;
        }
        return false;
    }

    //// PRIVATE METHODS ////

    /**
     * Re-orders cesium imagery layers based on the current imsImageryLayers
     * @private
     */
    _conformCesiumImageryLayers() {
        function sortNumber(a, b) {
            return a - b;
        }

        const availableHeightGroupIndexes = Array.from(this.imageryLayersByHeightGroup.keys()).sort(
            sortNumber,
        );

        for (const heightGroupIndex of availableHeightGroupIndexes) {
            const imageryLayers = this.imageryLayersByHeightGroup.get(heightGroupIndex);
            for (let i = 0; i < imageryLayers.length; i++) {
                // NOTE: This adds a cesium layer if it doesn't already exist in the viewer
                this._viewer.imageryLayers.raiseToTop(imageryLayers[i].cesiumImageryLayer);
            }
        }
    }

    /**
     * Merges the config layer options over the provided default options and handles
     * turning the url into a Resource object, the queryParameters being merged into the url Resource object,
     * and uses an optional proxyPrefixUrl to create a PrefixProxy
     * @param defaultOptions
     * @param layer
     * @private
     */
    _createStandardImageryLayerOptions(defaultOptions: ImsImageryLayerOptions, layer) {
        // Copy user options over the defaults
        const updatedDefaultOptions = Object.assign(defaultOptions, layer);

        // Some imagery layers (e.g. mapbox) don't require a URL, Cesium will explode when it is
        // missing, but required.
        if (layer.url) {
            // Use a Resource object instead of the default string for the url as it is more powerful
            const urlResource = new Cesium.Resource(layer.url);
            updatedDefaultOptions.url = urlResource;

            // Set queryParameters on url resource if it is defined
            if (layer.queryParameters) {
                urlResource.setQueryParameters(layer.queryParameters);
            }

            // Create and set prefix proxy if proxyPrefixUrl defined
            if (layer.proxyPrefixUrl) {
                urlResource.proxy = new PrefixProxy(layer.proxyPrefixUrl);
            }
        }

        return updatedDefaultOptions;
    }

    async _createCesiumIonLayer(layer) {
        const server = layer.server ? new Cesium.Resource(layer.server) : undefined;

        if (server) {
            server.setQueryParameters(layer.queryParameters);
        }

        if (!_.isNil(layer.assetId)) {
            const config: any = {
                assetId: layer.assetId,
                accessToken: layer.accessToken,
                server: server,
            };

            // Create prefix proxy if proxy url defined
            if (server && layer.proxyPrefixUrl) {
                config.server.proxy = new PrefixProxy(layer.proxyPrefixUrl);
            }

            // Combine key, server and assetId to create a unique url identifier for storing shown state in localstorage
            layer.url =
                'cesium_ion_' + layer.assetId + '_' + layer.server + '_' + layer.accessToken;

            const imagery = await Cesium.IonImageryProvider.fromAssetId(layer.assetId, config);

            this.addImsImageryLayerFromImageryProvider(imagery, layer);
        } else {
            console.error('Config error - Cesium Ion map layer requires an Asset ID');
        }
    }

    async _createTileMapServiceLayer(layer) {
        const defaultOptions: any = {};
        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);
        const imagery = await Cesium.TileMapServiceImageryProvider.fromUrl(options.url, options);
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    async _createArcGisLayer(layer) {
        const defaultOptions: any = {};
        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);
        const imagery = await Cesium.ArcGisMapServerImageryProvider.fromBasemapType(
            Cesium.ArcGisBaseMapType.SATELLITE,
            options,
        );
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    async _createBingMapsLayer(layer) {
        const defaultOptions: any = {
            key: ConfigService.bingAPIKey,
        };
        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);
        const imagery = await Cesium.BingMapsImageryProvider.fromUrl(options.url, options);
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
        return imagery;
    }

    _createMapBoxLayer(layer) {
        const defaultOptions: any = {};
        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);
        const imagery = new Cesium.MapboxImageryProvider(options);
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    _createOpenStreetMapLayer(layer) {
        const modifiedLayer = _.pick(layer, [
            'url',
            'minimumLevel',
            'maximumLevel',
            'fileExtension',
        ]);
        const imagery = new Cesium.OpenStreetMapImageryProvider(modifiedLayer);
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    _createWebMapTileLayer(layer) {
        const defaultOptions: any = {
            format: 'image/png',
            maximumLevel: 19,
        };
        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);
        const imagery = new Cesium.WebMapTileServiceImageryProvider(
            this.mapToConstructorOptions(options),
        );
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    private mapToConstructorOptions(
        layer,
    ): Cesium.WebMapTileServiceImageryProvider.ConstructorOptions {
        return {
            url: layer.url,
            format: layer.format,
            layer: layer.layers?.length > 0 ? layer.layers[0] : undefined,
            style: layer.style,
            tileMatrixSetID: layer.tileMatrixSetID,
            maximumLevel: layer.maximumLevel,
            minimumLevel: layer.minimumLevel,
        };
    }

    _createWebMapLayer(layer) {
        const defaultOptions: any = {
            crs: 'CRS:84',
            layers: '0',
        };

        const defaultQueryParameters = {
            version: '1.3.0',
            transparent: 'true',
            format: 'image/png',
        };

        // SRS works on WMS v1.1.0 - v1.1.1, at v1.3.0+ Cesium looks to ignore SRS and uses CRS query in requests
        // https://cesium.com/docs/cesiumjs-ref-doc/WebMapServiceImageryProvider.html#.ConstructorOptions
        if (_.has(layer, 'srs')) {
            defaultQueryParameters.version = '1.1.1';
        }

        // Deal with default query parameters in this case
        let queryParameters = defaultQueryParameters;
        if (layer.queryParameters) {
            queryParameters = angular.extend(defaultQueryParameters, layer.queryParameters);
        }

        const options = this._createStandardImageryLayerOptions(defaultOptions, layer);

        if (options.layers && Array.isArray(options.layers)) {
            options.layers = options.layers.join(',');
        }

        options.url.setQueryParameters(queryParameters);

        const imagery = new Cesium.WebMapServiceImageryProvider(options);
        this.addImsImageryLayerFromImageryProvider(imagery, layer);
    }

    _createSingleTileLayer(layer: ImsImageryLayer) {
        // this imagery provider creates a mickey-mouse SingleTileImageryProvider just to meet
        // the interface, the real work is done by adding an Entity to Cesium
        const imagery = new SpecialSingleTileImageryProvider(
            layer.url,
            // these are strings although we want (excpect) them to be floats- so explicitly
            // cast them to strings (to make parseFloat happy) and then parseFloat (to make
            // the interface happy)
            parseFloat(String(layer.longitude_of_center)),
            parseFloat(String(layer.latitude_of_center)),
            parseFloat(String(layer.width_metres)),
            parseFloat(String(layer.height_metres)),
            parseFloat(String(layer.rotation_degrees)),
            this._viewer,
        );

        this.addImsImageryLayerFromImageryProvider(imagery as any, layer);
    }
}
